import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MapPinOff, ShieldCheck } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BrandPeriod, useBrandApi } from '../../../common/hooks/use-brand-api.hook';
import { SectionCard, formatNumber } from '../../../components/design-system/admin';
import { MiniStat, Thumb } from '../../../components/design-system/analytics';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';
import { BrandPage, LoadFailed, PeriodPills, ShopNameText, useBrandMe } from '../../../modules/brand/components/BrandUi';
import { countryName } from '../../../modules/brand/brand-format';

/**
 * Where the brand is sold and where it is missing: which retailers carry
 * which collections (named only with their consent), who carries part of a
 * range, and where shoppers keep trying a product no retailer near them sells.
 */
export function BrandDistributionPage() {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const api = useBrandApi();
    const { me } = useBrandMe();
    const [period, setPeriod] = useState<BrandPeriod>('90d');
    const { data, loading, error, reload } = useLoader(async () => (me?.connected ? api.distribution(period) : null), [period, me?.connected]);

    return (
        <BrandPage
            testId="brand-distribution"
            crumb={t('BRAND_PORTAL.NAV.DISTRIBUTION')}
            title={t('BRAND_PORTAL.DISTRIBUTION.TITLE')}
            description={t('BRAND_PORTAL.DISTRIBUTION.SUBTITLE')}
            actions={<PeriodPills value={period} onChange={setPeriod} />}
        >
            {error && <LoadFailed onRetry={reload} />}
            {data && (
                <>
                    <Card className="py-4">
                        <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                            <MiniStat label={t('BRAND_PORTAL.CATALOGUE.RETAILERS')} value={formatNumber(data.retailers.total)} />
                            <MiniStat label={t('BRAND_PORTAL.DISTRIBUTION.LISTED')} value={formatNumber(data.retailers.listed)} sub={t('BRAND_PORTAL.DISTRIBUTION.LISTED_SUB')} />
                            <MiniStat label={t('BRAND_PORTAL.DISTRIBUTION.ANONYMOUS')} value={formatNumber(data.retailers.anonymous)} sub={t('BRAND_PORTAL.DISTRIBUTION.ANONYMOUS_SUB')} />
                            <MiniStat label={t('BRAND_PORTAL.CATALOGUE.SHARING')} value={formatNumber(data.retailers.sharingDemand)} sub={t('BRAND_PORTAL.CATALOGUE.SHARING_SUB')} />
                        </CardContent>
                    </Card>

                    <Card className="py-4" data-testid="brand-why-hidden">
                        <CardContent className="flex items-start gap-3">
                            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('BRAND_PORTAL.DISTRIBUTION.WHY_TITLE')}</p>
                                <p className="mt-1 text-sm text-muted-foreground">{t('BRAND_PORTAL.DISTRIBUTION.WHY_TEXT')}</p>
                            </div>
                        </CardContent>
                    </Card>

                    <SectionCard title={t('BRAND_PORTAL.DISTRIBUTION.COLLECTIONS')} description={t('BRAND_PORTAL.DISTRIBUTION.COLLECTIONS_TEXT')} testId="brand-collections">
                        {data.collections.length ? (
                            <div className="flex flex-col divide-y">
                                {data.collections.map((c) => (
                                    <div key={c.id} className="py-4 first:pt-0 last:pb-0">
                                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                                            <p className="text-sm font-medium">{c.name}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {t('BRAND_PORTAL.DISTRIBUTION.COLLECTION_LINE', { products: c.products, retailers: c.retailers, full: c.fullRange })}
                                            </p>
                                        </div>
                                        {c.gaps.length ? (
                                            <ul className="mt-3 flex flex-col gap-2">
                                                {c.gaps.map((g, i) => (
                                                    <li key={i} className="flex flex-col gap-1 rounded-lg border px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                                                        <ShopNameText label={g.label} className="text-sm" />
                                                        <span className="text-xs text-muted-foreground">
                                                            {t('BRAND_PORTAL.DISTRIBUTION.CARRIES', { carried: g.carried, total: c.products })}
                                                            {g.missing.length ? ` · ${t('BRAND_PORTAL.DISTRIBUTION.MISSING', { names: g.missing.slice(0, 3).map((m) => m.name).join(', ') })}${g.missing.length > 3 ? ` +${g.missing.length - 3}` : ''}` : ''}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="mt-2 text-xs text-muted-foreground">{t('BRAND_PORTAL.DISTRIBUTION.NO_GAPS')}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="py-6 text-center text-sm text-muted-foreground">{t('BRAND_PORTAL.DISTRIBUTION.NO_COLLECTIONS')}</p>
                        )}
                    </SectionCard>

                    <SectionCard flush title={t('BRAND_PORTAL.DISTRIBUTION.GAPS_TITLE')} description={t('BRAND_PORTAL.DISTRIBUTION.GAPS_TEXT', { km: data.gapDistanceKm })} testId="brand-demand-gaps">
                        {data.demandGaps.length ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('BRAND_PORTAL.DIMENSION.PRODUCT')}</TableHead>
                                        <TableHead>{t('BRAND_PORTAL.DIMENSION.CITY')}</TableHead>
                                        <TableHead className="text-end">{t('BRAND_PORTAL.KPI.SHOPPERS')}</TableHead>
                                        <TableHead className="text-end">{t('BRAND_PORTAL.KPI.VISUALIZATIONS')}</TableHead>
                                        <TableHead>{t('BRAND_PORTAL.DISTRIBUTION.NEAREST')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.demandGaps.map((g) => (
                                        <TableRow key={`${g.product.uuid}-${g.city}`}>
                                            <TableCell className="max-w-[240px]">
                                                <Link to={`/brand/products/${g.product.uuid}`} className="flex items-center gap-2 hover:underline">
                                                    <Thumb src={g.product.thumbnailUrl} size="xs" />
                                                    <span className="truncate">{g.product.name}</span>
                                                </Link>
                                            </TableCell>
                                            <TableCell>{[g.city, countryName(g.country, lang)].filter(Boolean).join(', ')}</TableCell>
                                            <TableCell className="text-end tabular-nums">{formatNumber(g.shoppers)}</TableCell>
                                            <TableCell className="text-end tabular-nums">{formatNumber(g.visualizations)}</TableCell>
                                            <TableCell className="max-w-[260px]">
                                                {g.nearest ? (
                                                    <span className="flex flex-wrap items-center gap-x-2">
                                                        <ShopNameText label={g.nearest.label} />
                                                        <span className="text-xs text-muted-foreground tabular-nums">{t('BRAND_PORTAL.DISTRIBUTION.KM', { km: formatNumber(g.nearest.distanceKm) })}</span>
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                                        <MapPinOff className="size-3.5" />
                                                        {t('BRAND_PORTAL.DISTRIBUTION.NO_NEAREST')}
                                                    </span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <p className="px-6 py-6 text-center text-sm text-muted-foreground">{t('BRAND_PORTAL.DISTRIBUTION.GAPS_EMPTY')}</p>
                        )}
                    </SectionCard>

                    <SectionCard flush title={t('BRAND_PORTAL.DISTRIBUTION.PRODUCTS')} testId="brand-distribution-products">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('BRAND_PORTAL.DIMENSION.PRODUCT')}</TableHead>
                                    <TableHead>{t('BRAND_PORTAL.DIMENSION.COLLECTION')}</TableHead>
                                    <TableHead className="text-end">{t('BRAND_PORTAL.DEMAND.RETAILERS')}</TableHead>
                                    <TableHead>{t('BRAND_PORTAL.DISTRIBUTION.NAMED')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.products.map((p) => (
                                    <TableRow key={p.uuid}>
                                        <TableCell className="max-w-[260px]">
                                            <Link to={`/brand/products/${p.uuid}`} className="flex items-center gap-2 hover:underline">
                                                <Thumb src={p.thumbnailUrl} size="xs" />
                                                <span className="truncate">{p.name}</span>
                                            </Link>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">{p.collectionName ?? '—'}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatNumber(p.retailers)}</TableCell>
                                        <TableCell className="max-w-[260px] truncate text-muted-foreground">
                                            {p.listedRetailers.length ? p.listedRetailers.map((l) => (l.kind === 'listed' ? l.name : '')).join(', ') : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </SectionCard>
                </>
            )}
            {loading && !data && <p className="text-sm text-muted-foreground">{t('BRAND_PORTAL.COMMON.LOADING')}</p>}
        </BrandPage>
    );
}

export default BrandDistributionPage;
